import { useCallback, useEffect, useRef, useState } from 'react';
import { getEmbedMarker } from './embedMarker';
import { registerEmbeddedEditorHandle, setEmbeddedEditing, type FlushableEditorHandle } from './pendingEdits';

export interface EmbeddedEditMode {
  /** True inside the phone app. Everything else here is inert outside it. */
  embedded: boolean;
  /** Whether this page may switch to editing at all (plain pages only in v1). */
  canEdit: boolean;
  editing: boolean;
  setEditing(next: boolean): void;
  /**
   * The host-imposed read-only to pass to the editor: undefined outside the
   * app (unchanged behavior), otherwise true unless the reader tapped Edit.
   * Read is the default because Lexical does not follow links while editable,
   * and a page that opens editable cannot be browsed by tapping.
   */
  readOnly: boolean | undefined;
  /** Pass to the editor's `onHandleChange` so background flushes reach it. */
  trackHandle(handle: FlushableEditorHandle | null): void;
}

/**
 * Read/Edit state for one page in the embedded console. `pageKey` resets the
 * mode to Read when the reader navigates to another page.
 */
export function useEmbeddedEditMode(pageKey: string, canEdit: boolean): EmbeddedEditMode {
  const embedded = getEmbedMarker() !== null;
  const [editingState, setEditingState] = useState<{ key: string; editing: boolean }>({ key: pageKey, editing: false });
  const editing = embedded && canEdit && editingState.key === pageKey && editingState.editing;
  const unregister = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!embedded) return;
    setEmbeddedEditing(editing);
  }, [editing, embedded]);
  useEffect(() => () => {
    if (embedded) setEmbeddedEditing(false);
    unregister.current?.();
    unregister.current = null;
  }, [embedded]);

  const setEditing = useCallback((next: boolean) => setEditingState({ key: pageKey, editing: next }), [pageKey]);
  const trackHandle = useCallback((handle: FlushableEditorHandle | null) => {
    unregister.current?.();
    unregister.current = handle ? registerEmbeddedEditorHandle(handle) : null;
  }, []);

  return {
    embedded,
    canEdit: embedded && canEdit,
    editing,
    setEditing,
    readOnly: embedded ? !editing : undefined,
    trackHandle,
  };
}

/** The Edit / Done control above a page body in the app. Renders nothing elsewhere. */
export function EmbeddedEditBar({ mode }: { mode: EmbeddedEditMode }) {
  if (!mode.embedded || !mode.canEdit) return null;
  return (
    <div className="embedded-edit-bar flex shrink-0 items-center justify-end gap-2 border-b border-nim bg-nim px-3 py-1.5">
      <button
        type="button"
        className="embedded-edit-toggle min-h-8 cursor-pointer rounded-md border border-nim bg-nim-secondary px-3 text-[0.8rem] font-semibold text-nim hover:bg-nim-hover"
        aria-pressed={mode.editing}
        data-testid="embedded-edit-toggle"
        onClick={() => mode.setEditing(!mode.editing)}
      >
        {mode.editing ? 'Done' : 'Edit'}
      </button>
    </div>
  );
}
